import { useEffect, useMemo, useState } from 'react'

const VEDIC_DAY_NAMES = [
  'RAVIVAR · SUN',
  'SOMVAR · MOON',
  'MANGALVAR · MARS',
  'BUDHVAR · MERCURY',
  'GURUVAR · JUPITER',
  'SHUKRAVAR · VENUS',
  'SHANIVAR · SATURN',
]
const ENGLISH_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const RAHU_KAAL_TIMES = [
  '4:30–6:00 PM',
  '7:30–9:00 AM',
  '3:00–4:30 PM',
  '12:00–1:30 PM',
  '1:30–3:00 PM',
  '10:30–12:00 PM',
  '9:00–10:30 AM',
]
const CAL_MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const WEEKDAY_HEADERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

function pad2(n) {
  return String(n).padStart(2, '0')
}

export default function CalendarDropdown({ open }) {
  const [now, setNow] = useState(() => new Date())
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear())
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const dayIdx = now.getDay()
  const dateStr = `${now.getDate()} ${CAL_MONTH_NAMES[now.getMonth()]} ${now.getFullYear()} · ${pad2(
    (now.getHours() % 12) || 12,
  )}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())} ${now.getHours() >= 12 ? 'pm' : 'am'}`

  const gridCells = useMemo(() => {
    const today = new Date()
    const firstDay = new Date(viewYear, viewMonth, 1).getDay()
    const totalDays = new Date(viewYear, viewMonth + 1, 0).getDate()
    const cells = []
    for (let i = 0; i < firstDay; i++) cells.push(null)
    for (let d = 1; d <= totalDays; d++) {
      const isToday = d === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear()
      cells.push({ day: d, isToday })
    }
    return cells
  }, [viewYear, viewMonth])

  const goPrevMonth = (e) => {
    e.stopPropagation()
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1)
        return 11
      }
      return m - 1
    })
  }

  const goNextMonth = (e) => {
    e.stopPropagation()
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1)
        return 0
      }
      return m + 1
    })
  }

  return (
    <div
      className={`${open ? '' : 'hidden'} header-dropdown absolute right-0 mt-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-[#8B1A2A] px-5 py-4 relative overflow-hidden">
        <div className="absolute top-2 right-3 opacity-20 pointer-events-none">
          <svg viewBox="0 0 52 52" className="w-12 h-12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="26" cy="26" r="10" fill="#FDE68A" />
            <circle cx="26" cy="26" r="15" stroke="#FDE68A" strokeWidth="0.8" strokeDasharray="2 3" />
            <line x1="26" y1="3" x2="26" y2="8" stroke="#FDE68A" strokeWidth="2" strokeLinecap="round" />
            <line x1="26" y1="44" x2="26" y2="49" stroke="#FDE68A" strokeWidth="2" strokeLinecap="round" />
            <line x1="3" y1="26" x2="8" y2="26" stroke="#FDE68A" strokeWidth="2" strokeLinecap="round" />
            <line x1="44" y1="26" x2="49" y2="26" stroke="#FDE68A" strokeWidth="2" strokeLinecap="round" />
            <line x1="9.7" y1="9.7" x2="13.2" y2="13.2" stroke="#FDE68A" strokeWidth="2" strokeLinecap="round" />
            <line x1="38.8" y1="38.8" x2="42.3" y2="42.3" stroke="#FDE68A" strokeWidth="2" strokeLinecap="round" />
            <line x1="38.8" y1="13.2" x2="42.3" y2="9.7" stroke="#FDE68A" strokeWidth="2" strokeLinecap="round" />
            <line x1="9.7" y1="42.3" x2="13.2" y2="38.8" stroke="#FDE68A" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <p className="text-[9px] font-bold tracking-[0.22em] text-red-200/70 uppercase mb-1.5">
          {VEDIC_DAY_NAMES[dayIdx]}
        </p>
        <h2 className="font-royal text-3xl font-bold text-white leading-none tracking-wide">
          {ENGLISH_DAY_NAMES[dayIdx]}
        </h2>
        <p className="text-[11px] text-red-100/60 mt-2 font-light tracking-wide">{dateStr}</p>
      </div>

      <div className="bg-red-50 border-b border-red-100 px-5 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span className="text-[10px] font-bold tracking-[0.18em] text-red-700 uppercase">Rahu Kaal</span>
        </div>
        <span className="text-[11px] font-semibold text-red-600 tracking-wider">{RAHU_KAAL_TIMES[dayIdx]}</span>
      </div>

      <div className="px-4 pt-3 pb-4">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={goPrevMonth}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-[#8B1A2A] transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="text-sm font-semibold text-[#8B1A2A] tracking-wide">
            {CAL_MONTH_NAMES[viewMonth]} {viewYear}
          </span>
          <button
            type="button"
            onClick={goNextMonth}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-[#8B1A2A] transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 mb-1">
          {WEEKDAY_HEADERS.map((h, i) => (
            <span key={i} className="text-center text-[10px] font-semibold text-gray-400 py-0.5">
              {h}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-y-0.5">
          {gridCells.map((cell, i) =>
            cell === null ? (
              <div key={i} className="h-7" />
            ) : (
              <div
                key={i}
                className={
                  cell.isToday
                    ? 'h-7 w-7 mx-auto flex items-center justify-center rounded-full bg-goldPrimary text-astroDark text-[11px] font-bold cursor-pointer select-none'
                    : 'h-7 w-7 mx-auto flex items-center justify-center rounded-full text-gray-600 text-[11px] hover:bg-red-50 hover:text-[#8B1A2A] cursor-pointer select-none transition-colors'
                }
              >
                {cell.day}
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  )
}
